/**
 * @author Milton Filho
 */
import { combineReducers } from 'redux';
import { Action, handleActions } from 'redux-actions';
import { OfflineQueueItem, OfflineSyncState } from 'modules/offlineSync/types';
import {
  CLEAR_FAILED_ITEM,
  DISCARD_OFFLINE_BOOKING,
  ENQUEUE_OFFLINE_ITEM,
  OFFLINE_BOOK_APPOINTMENT_ENQUEUED,
  SYNC_BOOKING_SUCCESS,
  SYNC_COMPLETE,
  SYNC_ITEM_FAILURE,
  SYNC_ITEM_SUCCESS,
  SYNC_START,
} from 'modules/offlineSync/actionTypes';
import {
  ClearFailedItemPayload,
  DiscardOfflineBookingPayload,
  OfflineBookAppointmentEnqueuedPayload,
  SyncBookingSuccessPayload,
  SyncItemFailurePayload,
  SyncItemSuccessPayload,
} from 'modules/offlineSync/actions';

const queue = handleActions<OfflineSyncState['queue'], any>(
  {
    [ENQUEUE_OFFLINE_ITEM]: (state, action: Action<OfflineQueueItem>) => [
      ...state,
      action.payload,
    ],
    [SYNC_ITEM_SUCCESS]: (state, action: Action<SyncItemSuccessPayload>) =>
      state.filter(item => item.id !== action.payload.queueItemId),
    [SYNC_BOOKING_SUCCESS]: (
      state,
      action: Action<SyncBookingSuccessPayload>,
    ) => state.filter(item => item.id !== action.payload.queueItemId),
    [SYNC_ITEM_FAILURE]: (state, action: Action<SyncItemFailurePayload>) =>
      state.map(item =>
        item.id === action.payload.queueItemId
          ? { ...item, failedAt: new Date().toISOString() }
          : item,
      ),
    [CLEAR_FAILED_ITEM]: (state, action: Action<ClearFailedItemPayload>) =>
      state.filter(item => item.id !== action.payload.queueItemId),
    // Same removal from the queue as `CLEAR_FAILED_ITEM`. The two differ in
    //   what they leave behind, not in what they take out of the queue: see the
    //   `pendingIds` case below and the `appointments` reducer.
    [DISCARD_OFFLINE_BOOKING]: (
      state,
      action: Action<DiscardOfflineBookingPayload>,
    ) => state.filter(item => item.id !== action.payload.queueItemId),
  },
  [],
);

const pendingIds = handleActions<OfflineSyncState['pendingIds'], any>(
  {
    [ENQUEUE_OFFLINE_ITEM]: (state, action: Action<OfflineQueueItem>) => [
      ...state,
      ...action.payload.tempAppointmentIds,
    ],
    [SYNC_ITEM_SUCCESS]: (state, action: Action<SyncItemSuccessPayload>) =>
      state.filter(id => !action.payload.pendingIdsToRemove.includes(id)),
    [SYNC_BOOKING_SUCCESS]: (
      state,
      action: Action<SyncBookingSuccessPayload>,
    ) => state.filter(id => !action.payload.tempIds.includes(id)),
    [CLEAR_FAILED_ITEM]: (state, action: Action<ClearFailedItemPayload>) => {
      // We don't have the temp IDs in this action, so pending items for
      // cleared failed items stay until page reload. This is acceptable
      // since the user is navigating to re-book anyway.
      //
      // It is also the right outcome where this action is now used
      //   deliberately: on a conflict for a booking we probably did create, the
      //   optimistic card stays and keeping its "pending" mark is the honest
      //   thing, because nothing has confirmed it.
      return state;
    },
    // Here the card is going away, so its pending mark has to go with it —
    //   otherwise `pendingIds` keeps ids for appointments that no longer exist.
    [DISCARD_OFFLINE_BOOKING]: (
      state,
      action: Action<DiscardOfflineBookingPayload>,
    ) => state.filter(id => !action.payload.tempAppointmentIds.includes(id)),
  },
  [],
);

const tempIdCounter = handleActions<OfflineSyncState['tempIdCounter'], any>(
  {
    [OFFLINE_BOOK_APPOINTMENT_ENQUEUED]: (
      state,
      action: Action<OfflineBookAppointmentEnqueuedPayload>,
    ) => state - action.payload.tempAppointments.length,
  },
  -1,
);

const syncStatus = handleActions<OfflineSyncState['syncStatus'], any>(
  {
    [SYNC_START]: () => 'syncing' as const,
    [SYNC_COMPLETE]: () => 'idle' as const,
  },
  'idle',
);

export type { OfflineSyncState };

export default combineReducers({
  queue,
  pendingIds,
  tempIdCounter,
  syncStatus,
});
